import type { ServerWorkflow, ServerNode } from './types';
import { classifyNodeType } from './types';
import { hydrateConditionData } from './conditionHydrator';
import { hydrateActionData } from './actionHydrator';
import type { ConditionNodeData } from '../nodes/ConditionNode';
import type { ActionNodeData } from '../nodes/ActionNode';
import type { TriggerNodeData } from '../nodes/TriggerNode';
import { Trigger } from '../nodes/TriggerNode';

/**
 * Discriminated union: `type` determines the shape of `data`.
 * TypeScript narrows `data` automatically when you branch on `step.type`, so
 * `as ConditionNodeData` / `as ActionNodeData` casts are unnecessary in most code.
 */
export type TriggerStepDefinition = {
    id: string;
    type: 'trigger';
    data: TriggerNodeData;
};

export type ConditionStepDefinition = {
    id: string;
    type: 'condition';
    data: ConditionNodeData;
};

export type ActionStepDefinition = {
    id: string;
    type: 'action';
    data: ActionNodeData;
};

export type StepDefinition = TriggerStepDefinition | ConditionStepDefinition | ActionStepDefinition;

export type StepData = StepDefinition['data'];

/**
 * Converts server workflow JSON into an ordered StepDefinition[] that
 * can be fed directly into `buildFlowFromSteps` for React Flow rendering.
 *
 * Algorithm:
 * 1. Index nodes by id for O(1) lookup
 * 2. Build a forward-adjacency map from edges (only ALWAYS and TRUE — skip FALSE dead-ends)
 * 3. Walk the linear chain starting at graph.entryNodeId
 * 4. For each visited node, classify its category and hydrate config into the correct data shape
 */
export function parseWorkflowToSteps(workflow: ServerWorkflow): StepDefinition[] {
    const { graph } = workflow;
    if (!graph?.nodes?.length) return [];

    const nodeById = new Map<string, ServerNode>();
    for (const node of graph.nodes) {
        nodeById.set(node.id, node);
    }

    const forwardEdge = new Map<string, string>();
    for (const edge of graph.edges) {
        if (edge.when === "FALSE" || !edge.to) continue;
        forwardEdge.set(edge.from, edge.to);
    }

    const steps: StepDefinition[] = [];
    const visited = new Set<string>();
    let currentId: string | undefined = graph.entryNodeId;

    while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const serverNode = nodeById.get(currentId);
        if (!serverNode) break;

        const category = classifyNodeType(serverNode.type);
        if (category === 'trigger') {
            steps.push({
                id: serverNode.id,
                type: 'trigger',
                data: hydrateTriggerData(serverNode, workflow.triggerType),
            });
        } else if (category === 'condition') {
            steps.push({
                id: serverNode.id,
                type: 'condition',
                data: hydrateConditionData(serverNode),
            });
        } else {
            steps.push({
                id: serverNode.id,
                type: 'action',
                data: hydrateActionData(serverNode),
            });
        }

        currentId = forwardEdge.get(currentId);
    }

    return steps;
}

function hydrateTriggerData(node: ServerNode, triggerType: string): TriggerNodeData {
    const isInbound = triggerType?.toUpperCase() === "INBOUND" || node.type.includes("Inbound");
    return {
        triggerType: isInbound ? Trigger.Message.Inbound : Trigger.Message.Outbound,
        title: isInbound ? "Incoming" : "Outgoing",
        description: isInbound ? "When a message comes in" : "When an Outgoing Message Is Sent",
    };
}
